<template>
    <main class="min-h-screen w-full">
        <WYSIWYGEditorToolbar
            v-if="activeComponents.editor.active"
            :key="activeComponents.content.colId"
            :active="activeComponents.editor.active"
            :editor="activeComponents.editor.instance"
        />
        <div
            class="fixed top-12 right-0 left-0 z-[20] border-b border-gray-200 bg-gray-200 px-2 lg:static"
            @click.stop
        >
            <nav
                class="emenu flex space-x-8"
                aria-label="Tabs"
            >
                <button
                    v-for="(tab, i) in editorTabs.tabs"
                    :key="tab"
                    :class="[
                        i === editorTabs.current
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                        'cursor-pointer border-b-2 px-2 py-4 text-sm font-medium whitespace-nowrap',
                    ]"
                    @click="editorSwitchVisibility(i)"
                >
                    {{ tab }}
                </button>
            </nav>
        </div>
        <div
            v-show="
                editorTabs.current === 1 || editorTabs.current === 2 || editorTabs.current === 3
            "
            class="active-edit:right-96 fixed top-28 right-0 bottom-4 left-0 z-[19] lg:top-14 lg:left-64"
        >
            <div
                id="HTMLEditor"
                ref="HTMLEditor"
                class="h-full w-full grow"
            ></div>
        </div>
        <div
            v-show="editorTabs.current === 0"
            class="active-edit:right-96 fixed top-36 right-0 bottom-0 left-0 z-[19] overflow-x-auto overflow-y-auto pb-24 text-left md:top-44 lg:left-64"
        >
            <div class="template-base-wrapper">
                <div
                    :class="[
                        dragging === true ? 'outline-sky-700' : 'focus:outline-hidden',
                        `template-base template-base-pdf relative w-full min-w-fit ${
                            activeComponents.showBlocks === true ? 'template-show-blocks' : ''
                        }`,
                    ]"
                >
                    <!-- Row wrapper -->
                    <Sortable
                        :list="templateEditModel.template.body"
                        item-key="id"
                        class="row-wrapper w-full"
                        :options="options"
                        @update="(event) => showSortedDocument(templateEditModel, event)"
                    >
                        <template #item="{ element }">
                            <div
                                class="list-group-item group/rows my-2 w-full p-1 outline-2 outline-solid"
                                :class="
                                    activeComponents.content.rowId === element.id
                                        ? 'outline-amber-400 hover:outline-amber-400'
                                        : 'outline-transparent hover:outline-amber-200'
                                "
                            >
                                <div
                                    v-if="element.extra.type === 'iterator'"
                                    class="mr-1 ml-8 rounded-sm bg-emerald-200 px-2 py-1 text-right text-sm font-medium text-gray-800"
                                >
                                    <MdiRepeat class="mr-1 inline h-4 w-4" />
                                    <span class="select-none"
                                        >{{ t('placeholderTypeMultiple') }}:
                                    </span>
                                    <b>{{ element.extra.placeholder }}</b>
                                </div>
                                <div class="flex">
                                    <!-- Handlebar -->
                                    <div class="h-fit w-8 group-hover/rows:block">
                                        <div class="bg-white">
                                            <!-- Drag handle -->
                                            <div class="handle my-px flex cursor-pointer p-1">
                                                <MdiMouseMoveVertical
                                                    :class="[
                                                        activeComponents.showBlocks === true
                                                            ? 'hover:text-gray-600'
                                                            : 'opacity-25 hover:text-gray-400',
                                                        'h-6 w-6 text-gray-400',
                                                    ]"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div class="grow">
                                        <!-- Col warpper -->
                                        <div
                                            :id="element.id"
                                            :class="`${element.cssClass}`"
                                            :style="`${extractStyles(element.style)}`"
                                            :data-extra-type="element.extra.type"
                                            :data-extra-placeholder="`${t('placeholderTypeMultiple')}: ${element.extra.placeholder}`"
                                        >
                                            <div
                                                v-for="col in element.cols"
                                                :id="col.id"
                                                :key="col.id"
                                                :class="[
                                                    activeComponents.content.colId === col.id
                                                        ? 'selected-col'
                                                        : '',
                                                    `${col.cssClass} p-1 pb-2`,
                                                ]"
                                                :style="extractStyles(col.style)"
                                            >
                                                <!-- Col content wrapper -->
                                                <div
                                                    :id="col.content.id"
                                                    :class="[
                                                        activeComponents.showBlocks === true
                                                            ? 'template-show-blocks'
                                                            : '',
                                                        col.content.cssClass,
                                                    ]"
                                                    :style="extractStyles(col.content.style)"
                                                    @mousedown="
                                                        setActiveContent(
                                                            activeComponents,
                                                            col.content,
                                                        )
                                                    "
                                                >
                                                    <WYSIWYGEditorCore
                                                        :model-value="col.content.html"
                                                        @update-content:model-value="
                                                            updateColContent
                                                        "
                                                        @focus-content:model-value="focusColContent"
                                                        @blur-content:model-value="blurColContent"
                                                    />
                                                </div>
                                                <span class="clear-both"></span>
                                            </div>
                                            <span class="clear-both"></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Sortable>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup>
import { reactive, ref, onMounted, nextTick, provide, watch } from 'vue';
import loader from '@monaco-editor/loader';
import {
    updateColContent,
    focusColContent,
    setActiveContent,
    showSortedDocument,
    blurColContent,
    sizeInfoHandler,
} from '../composables/useWYSIWYGEditorUtils';
import { extractStyles } from '../composables/useTemplateModel';
import { t } from '../composables/i18n';
import { asleep } from '../composables/utils';
import { MdiMouseMoveVertical } from 'materialdesignicons-vue3/icons/';
import WYSIWYGEditorCore from '../components/WYSIWYGEditorCore.vue';
import WYSIWYGEditorToolbar from '../components/WYSIWYGEditorToolbar.vue';
import { Sortable } from 'sortablejs-vue3';
import useWYSIWYGEditorExtensions from '../composables/useWYSIWYGEditorExtensions';
import * as monaco from 'monaco-editor';
import { MdiRepeat } from 'materialdesignicons-vue3/icons/';

const props = defineProps({
    templateEditModel: {
        type: Object,
        required: true,
    },
    activeComponents: {
        type: Object,
        required: true,
    },
});

const activeComponents = props.activeComponents;
const templateEditModel = props.templateEditModel;
const dragging = ref(false);

provide('useWYSIWYGEditorExtensions', useWYSIWYGEditorExtensions());

const options = {
    handle: '.handle',
    ghostClass: 'ghost',
    chosenClass: 'chosen',
    dragClass: 'dragging',
    animation: '200',
    dataIdAttr: 'id',
    onStart: () => {
        dragging.value = true;
    },
    onEnd: () => {
        dragging.value = false;
    },
};

const editorTabs = reactive({
    tabs: [t('Template'), t('templateVersionCSS'), t('templateFooter'), t('templateHeader')],
    current: 0,
});

const HTMLEditorData = reactive({
    html: '',
});

const editorSwitchVisibility = (index) => {
    if (index === 1) {
        if (HTMLEditorInstance) {
            HTMLEditorInstance.layout();
        }
    }

    editorTabs.current = index;
};

let HTMLEditorInstance = null;

const initHTMLEditor = async (monaco) => {
    const editorElement = document.getElementById('HTMLEditor');
    HTMLEditorInstance = monaco.editor.create(editorElement, {
        value: HTMLEditorData.html,
        language: 'html',
        theme: 'vs',
        tabSize: 4,
        automaticLayout: true,
        formatOnType: true,
        formatOnPaste: true,
        wordWrap: 'bounded',
        minimap: {
            showSlider: 'always',
            scale: 1,
        },
    });
    await asleep(100);
    HTMLEditorInstance.getAction('editor.action.formatDocument').run();
    HTMLEditorInstance.onDidChangeModelContent(() => {
        HTMLEditorData.html = HTMLEditorInstance.getValue();
    });
};

const visualDOMhelper = () => {
    //'.template-col-content table tbody th, .template-col-content table tbody td',
    const cells = document.querySelectorAll('.template-base');
    cells.forEach((cell) => {
        cell.addEventListener('mousemove', sizeInfoHandler.on);
        cell.addEventListener('mouseout', sizeInfoHandler.off);
    });
};

watch(
    () => editorTabs.current,
    async (newVal) => {
        if (newVal === 1) {
            HTMLEditorData.html = templateEditModel.template.head.styles || '';
            HTMLEditorInstance.setValue(HTMLEditorData.html);
        }
        if (newVal === 2) {
            HTMLEditorData.html = templateEditModel.template.meta.footer || '';
            HTMLEditorInstance.setValue(HTMLEditorData.html);
        }

        /// prepapration for dynamic header
        if (newVal === 3) {
            HTMLEditorData.html = templateEditModel.template.meta.header || '';
            HTMLEditorInstance.setValue(HTMLEditorData.html);
        }
    },
);

watch(
    () => HTMLEditorData.html,
    async (newVal) => {
        if (editorTabs.current === 1) {
            templateEditModel.template.head.styles = newVal;
        }
        if (editorTabs.current === 2) {
            templateEditModel.template.meta.footer = newVal;
        }
        if (editorTabs.current === 3) {
            templateEditModel.template.meta.header = newVal;
        }
    },
);

onMounted(async () => {
    await nextTick();
    //https://github.com/vdesjs/vite-plugin-monaco-editor?tab=readme-ov-file
    loader.config({ monaco });
    loader.init().then(async (monaco) => {
        await initHTMLEditor(monaco);
    });
    visualDOMhelper();
});
</script>

<style>
#size-info {
    font-size: 12px;
    color: #dedede;
    display: block;
    min-width: 60px;
    line-height: 14px;
    background-color: #121212;
    border: 2px solid #343434;
    border-radius: 5px;
    text-align: center;
    user-select: none;
    padding: 4px 8px;
    position: fixed;
    z-index: 5000;
}
</style>
